// external libs
import React from 'react'
import { useHistory } from 'react-router-dom'
import { FormRenderProps } from 'react-final-form'

// hooks & stores
import useTranslate from '../../../../../../i18n/useTranslate'
import useWebService from '../../../../../../services/web/useWebService'
import useLookupService from '../../../../../../services/lookup/useLookupService'
import useAuthenStore from '../../../../../../stores/authen/authenStore'
import useAppFolderAndTabAction from '../../../../../../stores/appFolderAndTab/appFolderAndTabAction'
import useManageProjectStore from '../../../../../../stores/manageProject/useManageProjectStore'
import useManageProjectAction from '../../../../../../stores/manageProject/useManageProjectAction'
import useLoading from '../../../../../../control/loading/useLoading'
import useFolderAndTab from '../../../useFolderAndTab'
import useIRB from '../../../../../../services/um/lookups/useIRB'
import useVerifyDocumentProjectDetail from '../../../../shareLogic/useVerifyDocumentProjectDetail'

// utilities
import checkPermission from '../../../../../../utilities/checkPermission'
import * as Utils from '../../../../../../utilities/date'
import { validateModeAccordionError } from '../../../../../../utilities/validate'

// constants
import { RoutePath } from '../../../../../../router/routesConfig'
import { DialogType } from '../../../../../../control/dialog/dialog'
import * as Constants from '../../../../../../constants/constants'
import constSubmittedStatus from '../../../../../../constants/submittedStatus'
import constSubmittedType from '../../../../../../constants/submittedType'
import { approveStatusREC1, approveStatusREC2 } from '../../../../../../constants/approveStatus'

// types
import * as VerifyDocumentType from '../verifyDocumentType'
import { ResultDocumentTopicLut } from '../../../../../../services/lookup/types/responseLookupServiceTypes'
import * as REQServiceType from '../../../../../../services/web/types/requestWebServiceTypes'
import useSnackbarAction from '../../../../../../stores/snackbar/snackbarAction'
import * as REQServiceTypes from '../../../../../../services/web/types/requestWebServiceTypes'

type OptType = {
    label: string
    value: string
}
type UploadFileObjType = {
    uploadFileObj: {
        documentTopicID: number

        fileList: {
            documentTopicID: number
            fileID: string
            fileToken: string
            filename: string
            path: string
        }[]

        subTopicPageList?: {
            page: string
            documentTopicID: number
            subtopicID: number
        }[]
    }
}

const useVerifyDocument = (props: VerifyDocumentType.VerifyDocumentType) => {
    const { projectData, onSaveComplete } = props
    const snackbarAction = useSnackbarAction()
    const history = useHistory()
    const tabAndFolderHook = useFolderAndTab()
    const loadingHook = useLoading()
    const callLookup = useLookupService()
    const callWebService = useWebService()
    const translate = useTranslate()
    const checkPermissionHook = checkPermission()
    const authenStore = useAuthenStore()
    const manageProjectStore = useManageProjectStore()
    const manageProjectAction = useManageProjectAction()
    const appFolderAndTabAction = useAppFolderAndTabAction()

    const [isCompletely, setIsCompletely] = React.useState<boolean>(true)
    const [initialUploadFile, setInitialUploadFile] = React.useState<any>()

    const documentTopicRef = React.useRef<ResultDocumentTopicLut[]>()
    const [initialValues, setInitialValues] = React.useState<any>({})
    const reviewerLutRef = React.useRef<OptType[]>()
    const isViewMode = checkPermissionHook.chkProjectStepViewMode(projectData)
    const isShowUpdateDate = checkPermissionHook.chkShowUpdateDate(projectData)
    const [configDialog, setConfigDialog] =
        React.useState<Pick<DialogType, 'title' | 'content' | 'type' | 'onClickConfirm' | 'onAfterClose'>>()
    const dialogRef = React.useRef<Record<string, Function>>()
    const formRef = React.useRef<FormRenderProps>()
    const { createInitialProjectDetail, updateProjectDetailFormatting } = useVerifyDocumentProjectDetail()
    const { getIRB } = useIRB()
    const formHistoryRef = React.useRef<Record<string, any>>({})

    React.useEffect(() => {
        if (projectData) {
            setIsCompletely(projectData?.verifyDocument ?? true)
            getReviewer(projectData.irb)
            createInitialData()
        }

        if (projectData?.irb && projectData?.submitTypeInfo?.value) {
            getDocumentTopicLut(projectData?.irb, projectData?.submitTypeInfo.value)
        }
    }, [projectData?.projectID ?? null, projectData?.submittedID ?? null])

    React.useEffect(() => {
        createInitialUploadFile()
    }, [
        manageProjectStore.documentTopic,
        JSON.stringify(manageProjectStore.projectData?.documentInfo),
        JSON.stringify(manageProjectStore.projectData?.latestDocument),
    ])

    const onRefDialog = (func: Record<string, Function>) => {
        //ห้าม set State หรือเรียก Action ในนี้
        dialogRef.current = func
    }

    const filterTopicUpload = (documentTopic: ResultDocumentTopicLut[]) => {
        let docTopic: ResultDocumentTopicLut[] = []
        const researcherTypeID = manageProjectStore?.projectData?.researcherTypeInfo.value

        if (documentTopic) {
            let isDocTopic = documentTopic
            if (researcherTypeID === 1) {
                Constants.topicForLecturer.forEach((element) => {
                    docTopic = documentTopic?.filter((item) => {
                        return item.id !== element.id
                    })
                })
            } else {
                Constants.topicForStudent.forEach((element) => {
                    docTopic = isDocTopic?.filter((item) => {
                        return item.id !== element.id
                    })
                    isDocTopic = docTopic
                })
            }
            if (docTopic.length > 1) {
                docTopic = docTopic?.map((itm, index: number) => {
                    return { ...itm, no: index + 1 }
                })
                manageProjectAction.setDocumentTopic(docTopic)
            }
        }
    }

    const getDocumentTopicLut = async (irb: number, submitType: number) => {
        if (irb) {
            const res = await callLookup.callDocumentTopicLut(irb, submitType)
            if (res.status === 1) {
                manageProjectAction.setDocumentTopic(res.result)
                filterTopicUpload(res.result)
                documentTopicRef.current = res.result
            } else {
                manageProjectAction.setDocumentTopic([])
                documentTopicRef.current = []
            }
        }
    }

    const createInitialUploadFile = () => {
        const documentInfo = projectData?.documentInfo
        let uploadFileInit = manageProjectStore.documentTopic?.map((itm: any, idx: number) => {
            return {
                uploadFileObj: {
                    fileList: [],
                    subTopicPageList: [],
                    documentTopicID: itm.id,
                    isDisplay: itm.isDisplay,
                    isRequired: itm.isRequired,
                },
            }
        }) as Array<UploadFileObjType>

        uploadFileInit?.forEach((itm: VerifyDocumentType.uploadFileObjType) => {
            documentInfo?.forEach((docInfo: any) => {
                if (docInfo.topicID === itm.uploadFileObj.documentTopicID) {
                    //@ts-ignore
                    itm.uploadFileObj.fileList = docInfo.fileInfo
                    //@ts-ignore
                    itm.uploadFileObj.subTopicPageList = docInfo.subtopic
                }
            })
        })

        //Filter การซ่อนแสดงหัวข้อเอกสาร ถ้าฝั่งสำนักงานให้ซ่อนหัวข้อเอกสาร แต่ว่าหัวข้อนั้นมีไฟล์อยู่ให้ทำการแสดงหัวข้อนั้นออกมา
        if (uploadFileInit?.length > 0) {
            uploadFileInit = uploadFileInit.filter((itm: any) => {
                return itm.uploadFileObj.isDisplay || itm.uploadFileObj.fileList.length !== 0
            })
        }
        setInitialUploadFile(uploadFileInit)
    }

    const getReviewer = async (irb: number) => {
        if (irb) {
            const res = await callLookup.callReviewerLut(irb)
            const formatedData = res.result.map((itm) => {
                return {
                    label: itm.name_th,
                    value: `${itm.id}`,
                }
            })
            reviewerLutRef.current = formatedData
        }
    }

    const handleChangeDocCompletely = (val: string) => {
        const isCompletely = val === '1' ? true : false
        setIsCompletely(isCompletely)
    }

    const createInitialData = async () => {
        loadingHook.loadingOpen()
        const defaultCompletely = projectData?.verifyDocument ?? '1'
        const lutIrb = await getIRB()
        const projectDetail = createInitialProjectDetail(projectData, lutIrb)
        const initialData = {
            isCompletelyDoc: defaultCompletely === '1' ? defaultCompletely : projectData?.verifyDocument ? '1' : '2',
            resubmitDocDate: projectData?.resubmitDocDate ? projectData?.resubmitDocDate : null,
            //@ts-ignore
            reviewer: projectData?.reviewerUserID
                ? {
                      label: `${projectData?.reviewerName_th}`,
                      value: `${projectData?.reviewerUserID}`,
                  }
                : null,
            remark: projectData?.verifyDocumentRemark,
            remarkWithFormat: projectData?.verifyDocumentWithFormat,
            documentAdditionalInfo: projectData?.documentAdditionalInfo,
            ...projectDetail,
        }

        setInitialValues(initialData)
        loadingHook.loadingClose()
    }

    const checkUseUploadEdit = () => {
        let isUseUploadEdit = false

        switch (projectData?.submitTypeInfo?.value) {
            case constSubmittedType.firstSubmitConsideration:
                isUseUploadEdit = false
                break
            case constSubmittedType.editAfterReview:
                isUseUploadEdit = true
                break
            case constSubmittedType.editAfterReview_nextPhaseProject:
                isUseUploadEdit = true
                break
            case constSubmittedType.exitAfterReview_AmendmentProject:
                isUseUploadEdit = true
                break
            default:
                isUseUploadEdit = false
        }
        if (projectData?.submittedStatusInfo?.value === constSubmittedStatus.incorrectDocument) {
            //เอกสารไม่ครบถ้วน
            isUseUploadEdit = true
        }
        if (
            projectData?.synthesisApproveStatusInfo?.value === approveStatusREC1.approveWithComment ||
            projectData?.synthesisApproveStatusInfo?.value === approveStatusREC2.approveWithComment
        ) {
            //Approve With Comment
            isUseUploadEdit = true
        }

        return isUseUploadEdit
    }
    const handleDirtyChange = (props: { dirty: boolean }) => {
        let isDirty: boolean = false
        if (!isViewMode) {
            isDirty = props.dirty
        }
        const paramsDirty = {
            tabDirty: isDirty,
            callbackDirty: isDirty ? callbackDirtyProcess : undefined,
        }
        appFolderAndTabAction.setDirty(paramsDirty)
    }

    const getSaveServiceParameter = (isDraft: boolean) => {
        const value = formRef.current?.values
        const isCompletelyDoc = value?.isCompletelyDoc === '1'
        let reviewerUserID = null
        if (value?.reviewer?.value) {
            reviewerUserID = parseInt(value.reviewer.value)
        }

        let sendEditData = null
        if (!isCompletelyDoc && value?.resubmitDocDate) {
            sendEditData = Utils.dateForAPI(value.resubmitDocDate) ?? null
        }

        const verifyProjectServiceParam: REQServiceType.VerifyProjectRequestType = {
            loginUserID: authenStore.profile.userId,
            projectID: projectData?.projectID ?? null,
            submittedID: projectData?.submittedID ?? null,
            reviewerUserID: reviewerUserID,
            isCompletely: isCompletelyDoc,
            resubmitDocDate: sendEditData,
            //remark: isCompletelyDoc ? null : value?.remark ? value.remark : null,
            remark: isCompletelyDoc ? null : value?.remarkWithFormat?.contentText ? value.remarkWithFormat.contentText : null,
            remarkHTML: isCompletelyDoc ? null : value?.remarkWithFormat?.contentHtml ? value.remarkWithFormat.contentHtml : null,
            isDraft: isDraft,
        }

        return verifyProjectServiceParam
    }
    const callbackDirtyProcess = (tabIndex: number, newTabSelected: any) => {
        const isDraft: boolean = true
        const param = getSaveServiceParameter(isDraft)
        processSaveData(param, {
            idx: tabIndex,
            selectedInfo: newTabSelected,
        })
    }

    const processSaveData = async (
        param: REQServiceType.VerifyProjectRequestType,
        changeTabOpt?: { idx: number; selectedInfo: any },
    ) => {
        try {
            dialogRef.current?.closeDialog()
            loadingHook.loadingOpen()
            const response = await callWebService.verifyProjectProgressReport(param)

            if (changeTabOpt) {
                if (changeTabOpt.idx !== null && changeTabOpt.selectedInfo !== null) {
                    tabAndFolderHook.setTab(changeTabOpt.idx, changeTabOpt.selectedInfo)
                }
            } else {
                if (onSaveComplete) onSaveComplete(response.status, param.isDraft as boolean)
            }
        } catch (ex) {
            console.log('useVerifyDocument.ts => processSaveData : ', ex)
        } finally {
            loadingHook.loadingClose()
        }
    }

    const handleSubmitForm = async (value: any) => {
        if (projectData?.projectID && projectData?.submittedID) {
            const isDraft: boolean = value?.submitType !== 'save'
            const param = getSaveServiceParameter(isDraft)

            if (value.submitType === 'save') {
                setConfigDialog({
                    title: translate.get('ALERT.TITLE.NOTIFICATION'),
                    content: translate.get('ALERT.CONTENT.NEED_TO_SAVE'),
                    type: 'alert-confirm',
                    onClickConfirm: () => {
                        processSaveData(param)
                    },
                })
                dialogRef.current?.openDialog()
            } else {
                //ถ้า save draft ไม่ต้องมี alert confirm
                processSaveData(param)
            }
        }
    }

    const handleClickBackToSearch = () => {
        history.push(RoutePath.manageProject)
    }

    const confirmEditDocResult = (formProps: FormRenderProps<any, any>, fieldNames: string[]) => {
        const isError = validateModeAccordionError(fieldNames, formProps)
        return new Promise((resolve, reject) => {
            if (!isError) {
                let isCompletelyDoc = formProps.values.isCompletelyDoc === '1'
                let sendEditData = null
                if (!isCompletelyDoc && formProps.values.resubmitDocDate) {
                    sendEditData = Utils.dateForAPI(formProps.values.resubmitDocDate) ?? null
                }
                const param = {
                    ...getEditDocParams(formHistoryRef.current['docResult']),
                    isCompletely: isCompletelyDoc,
                    resubmitDocDate: sendEditData,
                    //remark: isCompletelyDoc ? null : formProps.values?.remark ? formProps.values.remark : null,
                    remark: isCompletelyDoc ? null : formProps.values?.remarkWithFormat?.contentText ? formProps.values.remarkWithFormat.contentText : null,
                    remarkHTML: isCompletelyDoc ? null : formProps.values?.remarkWithFormat?.contentHtml ? formProps.values.remarkWithFormat.contentHtml : null,
                }
                processSaveData(param)
                    .then(() => {
                        handleEditDocResult(formProps)
                        handleEditDocReviewer(formProps)
                        resolve('')
                    })
                    .catch(() => {
                        reject('')
                    })
            } else {
                formProps.form.submit()
                reject('')
            }
        })
    }

    const confirmEditDocReviewer = (formProps: FormRenderProps<any, any>, fieldNames: string[]) => {
        const isError = validateModeAccordionError(fieldNames, formProps)
        return new Promise((resolve, reject) => {
            if (!isError) {
                let reviewerUserID = null
                if (formProps?.values?.reviewer?.value) {
                    reviewerUserID = parseInt(formProps.values.reviewer.value)
                }
                const param = {
                    ...getEditDocParams(formHistoryRef.current['docResult']),
                }
                const formatParam = {
                    submittedID: Number(param.submittedID),
                    loginUserID: Number(param.loginUserID),
                    reviewerUserID: Number(reviewerUserID),
                }
                Promise.all([callWebService.updateProjectReviewer(formatParam)])
                    .then(() => {
                        handleEditDocResult(formProps)
                        handleEditDocReviewer(formProps)
                        resolve('')
                    })
                    .catch(() => {
                        reject('')
                    })
            } else {
                formProps.form.submit()
                reject('')
            }
        })
    }

    const handleEditDocResult = (formProps: FormRenderProps<any, any>) => {
        formHistoryRef.current['docResult'] = formProps.values
    }

    const handleEditDocReviewer = (formProps: FormRenderProps<any, any>) => {
        formHistoryRef.current['docReviewer'] = formProps.values
    }

    const getEditDocParams = (formValues: any) => {
        let verifyProjectServiceParam: REQServiceType.VerifyProjectRequestType
        const isCompletelyDoc = formValues?.isCompletelyDoc === '1'
        let reviewerUserID = null
        if (formValues?.reviewer?.value) {
            reviewerUserID = parseInt(formValues.reviewer.value)
        }

        let sendEditData = null
        if (!isCompletelyDoc && formValues?.resubmitDocDate) {
            sendEditData = Utils.dateForAPI(formValues.resubmitDocDate) ?? null
        }

        verifyProjectServiceParam = {
            loginUserID: authenStore.profile.userId,
            projectID: projectData?.projectID ?? null,
            submittedID: projectData?.submittedID ?? null,
            reviewerUserID: reviewerUserID,
            isCompletely: isCompletelyDoc,
            resubmitDocDate: sendEditData,
            //remark: isCompletelyDoc ? null : formValues?.remark ? formValues.remark : null,
            remark: isCompletelyDoc ? null : formValues?.remarkWithFormat?.contentText ? formValues.remarkWithFormat.contentText : null,
            remarkHTML: isCompletelyDoc ? null : formValues?.remarkWithFormat?.contentHtml ? formValues.remarkWithFormat.contentHtml : null,
            isDraft: false,
        }

        return verifyProjectServiceParam
    }

    const confirmEditEthics = (formProps: FormRenderProps<any, any>, fieldNames: string[]) => {
        const isError = validateModeAccordionError(fieldNames, formProps)
        return new Promise((resolve, reject) => {
            if (!isError) {
                loadingHook.loadingOpen()
                const rawData = formRef.current?.values
                const formattingParam = updateProjectDetailFormatting(rawData, projectData)
                callWebService
                    .updateProjectDetail(formattingParam)
                    .then(async () => {
                        if (projectData) {
                            await tabAndFolderHook.reloadProjectData(projectData.projectID, projectData.submittedID)
                            await createInitialData()
                        }
                        resolve('')
                    })
                    .catch(() => {
                        reject('')
                    })
                    .finally(() => {
                        loadingHook.loadingClose()
                    })
            } else {
                formProps.form.submit()
                reject('')
            }
        })
    }

    const getDocumentInfo = (val: any) => {
        let fileDataList = val.uploadFile.filter((itm: any) => itm.uploadFileObj.fileList.length > 0)
        let myFileData: Array<REQServiceTypes.DocumentInfo> = []

        fileDataList.forEach((uploadObj: UploadFileObjType) => {
            const subTopicLst = uploadObj?.uploadFileObj.subTopicPageList?.map((subtopic) => {
                return {
                    subtopicID: subtopic.subtopicID,
                    page: subtopic.page,
                }
            })

            // const fileTokenInfo: Array<string> = uploadObj.uploadFileObj.fileList?.map((file) => file.fileToken) ?? []
            const fileTokenInfo: Array<{
                replaceFileID: number
                fileToken: string
                fileExtension?: string
                fileID: any
            }> =
                uploadObj.uploadFileObj.fileList?.map((file) => {
                    return {
                        //@ts-ignore
                        replaceFileID: file?.replaceFileID ? file.replaceFileID : null,
                        fileToken: file.fileToken,
                        fileExtension: file.filename.split('.').pop(),
                        fileID: file?.fileID ? file.fileID : null,
                    }
                }) ?? []

            myFileData.push({
                documentTopicID: uploadObj?.uploadFileObj?.documentTopicID,
                fileTokenInfo: fileTokenInfo,
                subtopic: subTopicLst && subTopicLst.length > 0 ? subTopicLst : [],
            })
        })
        return myFileData
    }

    const saveDataUploadDoc = async (paramDoc: REQServiceType.UpdateFileProjectRequestType) => {
        try {
            loadingHook.loadingOpen()
            await callWebService.updateFileProject(paramDoc)
        } catch (ex) {
            console.log('useVerifyDocument.ts => confirmEditUploadDoc : ', ex)
        } finally {
            loadingHook.loadingClose()
        }
    }

    const confirmEditUploadDoc = (formProps: FormRenderProps<any, any>, fieldNames: string[]) => {
        const isError = validateModeAccordionError(fieldNames, formProps)
        return new Promise((resolve, reject) => {
            if (isError) {
                snackbarAction.openSnackbar({
                    message: translate.get('ALERT.CONTENT.PLEASE_COMPLETE_PAGE'),
                    type: 'error',
                })
                reject('')
            } else {
                const value = formRef.current?.values
                const doc = getDocumentInfo(value)
                const paramDoc: REQServiceType.UpdateFileProjectRequestType = {
                    projectID: projectData?.projectID ?? null,
                    submittedID: projectData?.submittedID ?? null,
                    documentInfo: doc ?? null,
                }
                saveDataUploadDoc(paramDoc)
                    .then(async () => {
                        if (projectData) {
                            await tabAndFolderHook.reloadProjectData(projectData.projectID, projectData.submittedID)
                            await createInitialData()
                        }
                        resolve('')
                    })
                    .catch(() => {
                        reject('')
                    })
            }
        })
    }

    return {
        formRef,
        configDialog,
        onRefDialog,
        isCompletely,
        initialUploadFile: initialUploadFile,
        isViewMode,
        initialValues,
        isShowUpdateDate,
        onSubmitForm: handleSubmitForm,
        onChangeDocCompletely: handleChangeDocCompletely,
        checkUseUploadEdit: checkUseUploadEdit,
        onDirtyChange: handleDirtyChange,
        onClickBackToSearch: handleClickBackToSearch,
        projectDetailLabel: translate.get('LABEL.PROJECT_DETAIL'),
        researcherDetailLabel: translate.get('LABEL.RESEARCHER_DETAIL'),
        confirmEditDocResult,
        confirmEditDocReviewer,
        handleEditDocResult,
        handleEditDocReviewer,
        confirmEditEthics,
        confirmEditUploadDoc,
    }
}

export default useVerifyDocument
